import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { HTTPError } from 'ky';
import sendNodeCommand from 'store/sendNodeCommand';
import { components } from 'api/types';
import { fetchNodeLocalFiles, recycleNodeLocalFiles, type NodeLocalFilesPreview } from 'store/nodeLocalFiles';

interface Props {
  nodeAgentId: string;
  nodeName: string;
  onClose: () => void;
}

type Holder = components['schemas']['NodeLocalFilesHolder'];

const POLL_INTERVAL_MS = 3000;
const STALL_POLLS = 5;

function HolderTable({ holders }: { holders: Holder[] }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Workflow</TableCell>
          <TableCell>State</TableCell>
          <TableCell align="right">Paths</TableCell>
          <TableCell>Tags</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {holders.map((h) => (
          <TableRow key={h.workflow_id}>
            <TableCell>
              {h.name ?? '—'}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>
                {h.workflow_id}
              </Typography>
            </TableCell>
            <TableCell>
              {h.state}
              {(h.live_task_count ?? 0) > 0 && ` (${h.live_task_count} live)`}
            </TableCell>
            <TableCell align="right">{h.path_count}</TableCell>
            <TableCell>{(h.tags ?? []).join(', ')}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

type Phase = 'loading' | 'preview' | 'recycling' | 'done' | 'stalled';

export default function RecycleLocalFilesDialog({ nodeAgentId, nodeName, onClose }: Props) {
  const [preview, setPreview] = useState<NodeLocalFilesPreview | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [queued, setQueued] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'recycle' | 'sweep'>(null);
  const [swept, setSwept] = useState(false);
  const stall = useRef({ last: -1, unchanged: 0 });

  // Returns the preview instead of committing it: the caller's effect owns the
  // write, so a response that lands after its effect was cleaned up (node
  // changed, dialog closed) cannot overwrite the current node's holders.
  const load = () => fetchNodeLocalFiles(nodeAgentId);

  useEffect(() => {
    let cancelled = false;
    load()
      .then((p) => {
        if (!cancelled) {
          setPreview(p);
          setPhase('preview');
        }
      })
      .catch((err) => {
        console.error('Failed to load local files for node:', err);
        if (!cancelled) {
          setError('Failed to load the local files held on this node.');
          setPhase('preview');
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeAgentId]);

  useEffect(() => {
    if (phase !== 'recycling') return;
    let cancelled = false;
    const timer = window.setInterval(() => {
      load()
        .then((p) => {
          if (cancelled) return;
          setPreview(p);
          const remaining = (p.recyclable ?? []).length;
          if (remaining === 0) {
            setPhase('done');
            return;
          }
          if (remaining === stall.current.last) {
            stall.current.unchanged += 1;
            if (stall.current.unchanged >= STALL_POLLS) setPhase('stalled');
          } else {
            stall.current = { last: remaining, unchanged: 0 };
          }
        })
        .catch((err) => console.error('Failed to poll local files for node:', err));
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, nodeAgentId]);

  const follow = (total: number) => {
    setQueued(total);
    stall.current = { last: -1, unchanged: 0 };
    setPhase(total > 0 ? 'recycling' : 'done');
  };

  const handleRecycle = () => {
    setBusy('recycle');
    setError(null);
    recycleNodeLocalFiles(nodeAgentId)
      .then((accepted) => follow(accepted.queued_workflows ?? 0))
      .catch((err: unknown) => {
        if (err instanceof HTTPError && err.response.status === 409) {
          setError(null);
          follow(remaining);
          return;
        }
        console.error('Failed to recycle local files:', err);
        setError('Failed to start recycling. Please try again.');
      })
      .finally(() => setBusy(null));
  };

  const handleSweep = () => {
    setBusy('sweep');
    setError(null);
    sendNodeCommand(nodeAgentId, 'sweep_retained_dirs', {})
      .then(() => setSwept(true))
      .catch((err) => {
        console.error('Failed to request sweep:', err);
        setError('Failed to request a sweep. The node still sweeps on its own schedule.');
      })
      .finally(() => setBusy(null));
  };

  const recyclable = preview?.recyclable ?? [];
  const live = preview?.live ?? [];
  const remaining = recyclable.length;
  const recycledSoFar = Math.max(0, queued - remaining);
  const finished = phase === 'done' || phase === 'stalled';

  return (
    <Dialog open onClose={busy || phase === 'recycling' ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Recycle local files on {nodeName}</DialogTitle>
      <DialogContent>
        {phase === 'preview' && (
          <DialogContentText>
            Frees the disk that finished workflows still hold on this node through their local files. Uploaded results
            are kept, but the local files are deleted by the node&apos;s next sweep and cannot be recovered; afterwards
            those workflows&apos; tasks can only be reset from the task that produced the files. Workflows with live
            tasks are skipped. Recycling runs in the background, one run per node at a time; this dialog follows its
            progress (a click while a run is active follows that run).
          </DialogContentText>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {phase === 'loading' && <CircularProgress size={24} sx={{ mt: 2 }} />}

        {phase === 'preview' && preview && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              {remaining === 0
                ? 'No finished workflows hold local files on this node.'
                : `${remaining} finished workflow(s) holding ${preview.recyclable_paths} registered path(s):`}
            </Typography>
            {remaining > 0 && <HolderTable holders={recyclable} />}
            {live.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>
                  Skipped — {live.length} workflow(s) still live:
                </Typography>
                <HolderTable holders={live} />
              </>
            )}
          </>
        )}

        {phase === 'recycling' && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Recycling in the background — {recycledSoFar} of {queued} workflow(s) done…
            </Typography>
            <LinearProgress
              variant="determinate"
              value={queued > 0 ? (100 * recycledSoFar) / queued : 0}
              sx={{ mt: 1 }}
            />
          </>
        )}

        {finished && (
          <>
            <Alert severity={phase === 'done' ? 'success' : 'warning'} sx={{ mt: 1 }}>
              {phase === 'done'
                ? `Recycled ${queued} workflow(s). The node reclaims the run dirs on its next sweep (dirs older than the sweep's minimum age).`
                : `Recycled ${recycledSoFar} of ${queued} workflow(s); ${remaining} did not change over the last polls — see the backend log for the workflows it could not write. Sweep now still reclaims what was recycled.`}
              {live.length > 0 && ` ${live.length} live workflow(s) were skipped.`}
            </Alert>
            {swept && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Sweep requested. Agents that predate the command ignore it and sweep on their own schedule.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy !== null || phase === 'recycling'}>
          {finished ? 'Close' : 'Cancel'}
        </Button>
        {phase === 'preview' && (
          <Button
            onClick={handleRecycle}
            variant="contained"
            color="warning"
            disabled={busy !== null || remaining === 0}
          >
            {busy === 'recycle' ? 'Starting…' : `Recycle ${remaining} workflow(s)`}
          </Button>
        )}
        {finished && recycledSoFar > 0 && (
          <Button onClick={handleSweep} variant="contained" disabled={busy !== null || swept}>
            {busy === 'sweep' ? 'Requesting…' : swept ? 'Sweep requested' : 'Sweep now'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
